import { useSyncExternalStore } from 'react';

// ── Types ──────────────────────────────────────────────────────
export type WidgetId = 'weather' | 'agenda' | 'news' | 'buttons';
export type WidgetSize = 'compact' | 'wide' | 'tall' | 'large';

export interface WidgetConfig {
  id: WidgetId;
  enabled: boolean;
  size: WidgetSize;
}

export interface WidgetLayout {
  widgets: WidgetConfig[];
}

// Allowed sizes per widget
export const ALLOWED_SIZES: Record<WidgetId, WidgetSize[]> = {
  weather: ['compact', 'wide'],
  agenda: ['compact', 'wide', 'tall', 'large'],
  news: ['compact', 'wide', 'tall', 'large'],
  buttons: ['wide', 'large'],
};

// Size → CSS Grid spans
export const SIZE_SPANS: Record<WidgetSize, { col: number; row: number }> = {
  compact: { col: 1, row: 1 },
  wide: { col: 2, row: 1 },
  tall: { col: 1, row: 2 },
  large: { col: 2, row: 2 },
};

const STORAGE_KEY = 'caro_widget_layout';

const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: 'weather', enabled: true, size: 'compact' },
  { id: 'agenda', enabled: true, size: 'wide' },
  { id: 'news', enabled: true, size: 'wide' },
  { id: 'buttons', enabled: true, size: 'wide' },
];

// ── Module-level store ─────────────────────────────────────────
let layout: WidgetLayout = { widgets: [...DEFAULT_WIDGETS] };
const listeners = new Set<() => void>();

function loadFromStorage(): WidgetLayout {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { widgets: [...DEFAULT_WIDGETS] };
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.widgets)) return { widgets: [...DEFAULT_WIDGETS] };

    // Validate each widget
    const validIds: WidgetId[] = ['weather', 'agenda', 'news', 'buttons'];
    const widgets: WidgetConfig[] = parsed.widgets
      .filter((w: any) => validIds.includes(w.id))
      .map((w: any) => ({
        id: w.id as WidgetId,
        enabled: typeof w.enabled === 'boolean' ? w.enabled : true,
        size: ALLOWED_SIZES[w.id as WidgetId]?.includes(w.size) ? w.size : ALLOWED_SIZES[w.id as WidgetId][0],
      }));

    // Ensure all widget IDs are present (add any missing ones at end)
    for (const id of validIds) {
      if (!widgets.find(w => w.id === id)) {
        widgets.push({ id, enabled: true, size: ALLOWED_SIZES[id][0] });
      }
    }

    return { widgets };
  } catch {
    return { widgets: [...DEFAULT_WIDGETS] };
  }
}

function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // localStorage not available
  }
}

function notify() {
  listeners.forEach((fn) => fn());
}

function updateLayout(newLayout: WidgetLayout) {
  layout = newLayout;
  saveToStorage();
  notify();
}

// Initialize on module load
layout = loadFromStorage();

// ── Public API ─────────────────────────────────────────────────
function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): WidgetLayout {
  return layout;
}

export function useWidgetLayout() {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  return {
    layout: current,
    widgets: current.widgets,
    enabledWidgets: current.widgets.filter(w => w.enabled),

    setWidgetEnabled(id: WidgetId, enabled: boolean) {
      updateLayout({
        widgets: layout.widgets.map(w => w.id === id ? { ...w, enabled } : w),
      });
    },

    setWidgetSize(id: WidgetId, size: WidgetSize) {
      if (!ALLOWED_SIZES[id].includes(size)) return;
      updateLayout({
        widgets: layout.widgets.map(w => w.id === id ? { ...w, size } : w),
      });
    },

    cycleWidgetSize(id: WidgetId) {
      const widget = layout.widgets.find(w => w.id === id);
      if (!widget) return;
      const allowed = ALLOWED_SIZES[id];
      const currentIdx = allowed.indexOf(widget.size);
      const nextSize = allowed[(currentIdx + 1) % allowed.length];
      updateLayout({
        widgets: layout.widgets.map(w => w.id === id ? { ...w, size: nextSize } : w),
      });
    },

    reorderWidgets(newOrder: WidgetId[]) {
      const widgetMap = new Map(layout.widgets.map(w => [w.id, w]));
      const reordered = newOrder
        .map(id => widgetMap.get(id))
        .filter((w): w is WidgetConfig => w !== undefined);
      // Add any missing widgets at the end
      for (const w of layout.widgets) {
        if (!reordered.find(r => r.id === w.id)) {
          reordered.push(w);
        }
      }
      updateLayout({ widgets: reordered });
    },

    moveWidget(fromIdx: number, toIdx: number) {
      const widgets = [...layout.widgets];
      const [moved] = widgets.splice(fromIdx, 1);
      widgets.splice(toIdx, 0, moved);
      updateLayout({ widgets });
    },

    resetLayout() {
      updateLayout({ widgets: [...DEFAULT_WIDGETS] });
    },
  };
}
